import axios from "axios";
import { get, set } from "lodash";

import { IDocument } from "../models/document.model";
import { IOption } from "../models/option.model";
import { FULFILLED, PENDING, REJECTED } from "./action-type.util";

const remapItem = item => ({ id: item["documentID"], ...item });
const remapItems = items => items.map(remapItem);

export const ACTION_TYPES = {
  FETCH_DOCUMENTS: "documentation/FETCH_DOCUMENTS",
  FETCH_EMAIL_DATA: "documentation/FETCH_EMAIL_DATA",
  SEND_EMAIL: "documentation/SEND_EMAIL",
  UPLOAD_FILE: "documentation/UPLOAD_FILE",
  DOWNLOAD_FILE: "documentation/DOWNLOAD_FILE",
  DELETE_FILE: "documentation/DELETE_FILE",
  TOGGLE_UPLOAD_MODAL: "documentation/TOGGLE_UPLOAD_MODAL",
  TOGGLE_UPLOAD_TOAST: "documentation/TOGGLE_UPLOAD_TOAST",
  FETCH_DOCUMENT_TYPES: "documentation/FETCH_TYPES",
  TOGGLE_CONFIRMATION_MODAL: "documentation/TOGGLE_CONFIRMATION_MODAL",
  TOGGLE_CONFIRMATION_FAIL_MODAL: "documentation/TOGGLE_CONFIRMATION_FAIL_MODAL",
};

const initialState = {
  documents: [],
  inProcess: false,
  email: {
    defaultRecipients: new Array<string>(),
    ccRecipients: new Array<string>(),
    subject: "",
    bodyText: "",
    smsMessage: "",
  },
  uploadModal: {
    documentTypes: new Array<IOption>(),
  },
  isUploadModalVisible: false,
  isUploadToastVisible: false,
  isConfirmationModalVisible: false,
  isConfirmationFailModalVisible: false,
};

export type DocumentationState = Readonly<typeof initialState>;

/**
 * Documentation Reducer
 */
export default (state: DocumentationState = initialState, action): DocumentationState => {
  switch (action.type) {
    // Pending
    case PENDING(ACTION_TYPES.FETCH_DOCUMENT_TYPES):
    case PENDING(ACTION_TYPES.DOWNLOAD_FILE):
    case PENDING(ACTION_TYPES.UPLOAD_FILE):
    case PENDING(ACTION_TYPES.SEND_EMAIL):
    case PENDING(ACTION_TYPES.FETCH_EMAIL_DATA):
    case PENDING(ACTION_TYPES.FETCH_DOCUMENTS): {
      return {
        ...state,
        documents: [],
        inProcess: true,
      };
    }

    // Fulfilled
    case FULFILLED(ACTION_TYPES.FETCH_DOCUMENT_TYPES): {
      const documentOptions = get(action, "payload.data");
      return {
        ...state,
        inProcess: false,
        uploadModal: {
          ...state.uploadModal,
          documentTypes: documentOptions,
        },
      };
    }

    case FULFILLED(ACTION_TYPES.DOWNLOAD_FILE): {
      return {
        ...state,
        inProcess: false,
      };
    }

    case FULFILLED(ACTION_TYPES.UPLOAD_FILE): {
      return {
        ...state,
        inProcess: false,
        isUploadToastVisible: true,
      };
    }

    case FULFILLED(ACTION_TYPES.SEND_EMAIL): {
      return {
        ...state,
        inProcess: false,
      };
    }

    case FULFILLED(ACTION_TYPES.FETCH_EMAIL_DATA): {
      const defaultRecipients = get(action, "payload.data.defaultRecipients");
      const defaultEmails = defaultRecipients.map(email => {
        return email.emailAddress;
      });
      const ccRecipients = get(action, "payload.data.ccRecipients");
      const ccEmails = ccRecipients.map(email => {
        return email.emailAddress;
      });
      const subject = get(action, "payload.data.subject");
      const bodyText = get(action, "payload.data.bodyText");
      const smsMessage = get(action, "payload.data.smsMessage");
      return {
        ...state,
        inProcess: false,
        email: {
          ...state.email,
          defaultRecipients: defaultEmails,
          ccRecipients: ccEmails,
          subject,
          bodyText,
          smsMessage,
        },
      };
    }

    case FULFILLED(ACTION_TYPES.FETCH_DOCUMENTS): {
      return {
        ...state,
        inProcess: false,
        documents: action.payload.data as IDocument[],
      };
    }

    // Rejected
    case REJECTED(ACTION_TYPES.FETCH_DOCUMENT_TYPES):
    case REJECTED(ACTION_TYPES.DOWNLOAD_FILE):
    case REJECTED(ACTION_TYPES.UPLOAD_FILE):
    case REJECTED(ACTION_TYPES.SEND_EMAIL):
    case REJECTED(ACTION_TYPES.FETCH_EMAIL_DATA):
    case REJECTED(ACTION_TYPES.FETCH_DOCUMENTS): {
      return {
        ...state,
        inProcess: false,
      };
    }

    // Non network actions

    case ACTION_TYPES.TOGGLE_UPLOAD_MODAL: {
      const isUploadModalVisible = get(action, "meta.open");
      return {
        ...state,
        isUploadModalVisible,
      };
    }

    case ACTION_TYPES.TOGGLE_UPLOAD_TOAST: {
      const isUploadToastVisible = get(action, "meta.open");
      return {
        ...state,
        isUploadToastVisible,
      };
    }

    case ACTION_TYPES.TOGGLE_CONFIRMATION_MODAL: {
      const isConfirmationModalVisible = get(action, "meta.open");
      return {
        ...state,
        isConfirmationModalVisible,
      };
    }

    case ACTION_TYPES.TOGGLE_CONFIRMATION_FAIL_MODAL: {
      const isConfirmationFailModalVisible = get(action, "meta.open");
      return {
        ...state,
        isConfirmationFailModalVisible,
      };
    }

    default: {
      return {
        ...state,
      };
    }
  }
};

/**
 * Fetch documents associated with the specified proposal .
 * @param {string} proposalID
 */
export const fetchDocuments = (proposalID: string) => {
  return {
    type: ACTION_TYPES.FETCH_DOCUMENTS,
    payload: axios.get(`proposals/${proposalID}/documents`).then(response => {
      const items = get(response, "data", null);
      if (items) set(response, "data", remapItems(items));
      return response;
    }),
  };
};

/**
 * Fetch document types for the Upload modal.
 * @param {string} proposalID - id of the proposal
 */
export const fetchDocumentTypes = (proposalID: string) => {
  return {
    type: ACTION_TYPES.FETCH_DOCUMENT_TYPES,
    payload: axios.get(`proposals/${proposalID}/documents/types`),
  };
};

/**
 * Upload a document for the specified proposal.
 * @param {string} proposalID  - The specified proposal's ID.
 * @param {string} documentTypeID  - the type of document.
 * @param {any} file - documents data
 */
export const uploadDocument = (proposalID: string, file: any) => {
  const formData = new FormData();
  formData.append("FormFile", file);
  return {
    type: ACTION_TYPES.UPLOAD_FILE,
    payload: axios.post(`proposals/${proposalID}/documents`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    }),
  };
};

/**
 * Download the binary data for the specified document.
 * @param {string} proposalID - Id of the proposal that owns the document.
 * @param {string} documentID - Id of the specified document.
 */
export const downloadDocument = (proposalID: string, documentID: string) => {
  return {
    type: ACTION_TYPES.DOWNLOAD_FILE,
    payload: axios.get(`proposals/${proposalID}/documents/${documentID}`, { responseType: "blob" }),
  };
};

/**
 * Delete the specified document for the specified proposal.
 * @param {string} proposalID - Id of the proposal tha owns the document.
 * @param {string} documentID - Id of the document to delete.
 */
export const deleteDocument = (proposalID: string, documentID: string) => {
  return {
    type: ACTION_TYPES.DELETE_FILE,
    payload: axios.delete(`proposals/${proposalID}/documents/${documentID}`),
  };
};

/**
 * Fetch default email subject, body and email recipients
 */
export const fetchEmailData = (proposalID: string) => {
  return {
    type: ACTION_TYPES.FETCH_EMAIL_DATA,
    payload: axios.get(`proposals/${proposalID}/documents/email`),
  };
};

/**
 * Send an email with the specified attachments.
 */
export const sendEmail = (
  proposalID: string,
  attachmentDocumentIDs: number[],
  defaultRecipients: string[],
  otherRecipients: string[],
  subject: string,
  bodyText: string,
  smsMessage: string,
  sendWithoutRevision: Boolean,
) => {
  const params = {
    attachmentDocumentIDs,
    defaultRecipients,
    otherRecipients,
    subject,
    bodyText,
    smsMessage,
    sendWithoutRevision,
  };

  return {
    type: ACTION_TYPES.SEND_EMAIL,
    payload: axios.post(`proposals/${proposalID}/documents/email`, params),
  };
};

/**
 * Show or Hide the upload document modal.
 * @param {boolean} open - true opens modal, false closes modal.
 */
export const toggleUploadModal = (open: boolean) => {
  return {
    type: ACTION_TYPES.TOGGLE_UPLOAD_MODAL,
    meta: { open },
  };
};

/**
 * Show or Hide the documentation email confirmation modal
 * @param {boolean} open - true opens modal, false closes modal.
 */
export const toggleConfirmationModal = (open: boolean) => {
  return {
    type: ACTION_TYPES.TOGGLE_CONFIRMATION_MODAL,
    meta: { open },
  };
};

/**
 * Show or Hide the documentation email confirmation modal
 * @param {boolean} open - true opens modal, false closes modal.
 */
export const toggleConfirmationFailModal = (open: boolean) => {
  return {
    type: ACTION_TYPES.TOGGLE_CONFIRMATION_FAIL_MODAL,
    meta: { open },
  };
};

/**
 * Set isUploadToastVisible to false
 */
export const toggleUploadToast = (open: boolean) => {
  return {
    type: ACTION_TYPES.TOGGLE_UPLOAD_TOAST,
    meta: { open },
  };
};

/**
 * Download the proposal document for the specified proposal .
 * @param {string} proposalID
 */
export const downloadAnalyzerProposal = (proposalID: string) => {
  return {
    type: ACTION_TYPES.DOWNLOAD_FILE,
    payload: axios.get(`proposals/submissions/${proposalID}`, {
      params: { type: "AnalyzerProposal" },
      responseType: "blob",
    }),
  };
};

/**
 * Download the analyzer pdf for the specified proposal
 * @param proposalID
 */
export const downloadAnalyzer = (proposalID: string) => {
  return {
    type: ACTION_TYPES.DOWNLOAD_FILE,
    payload: axios.get(`proposals/submissions/${proposalID}`, {
      params: { type: "Analyzer" },
      responseType: "blob",
    }),
  };
};

/**
 * Download the equalizer pdf for the specified proposal
 * @param proposalID
 */
export const downloadEqualizer = (proposalID: string) => {
  return {
    type: ACTION_TYPES.DOWNLOAD_FILE,
    payload: axios.get(`proposals/submissions/${proposalID}`, {
      params: { type: "Equalizer" },
      responseType: "blob",
    }),
  };
};

/**
 * Download the equalizer proposal pdf for the specified proposal
 * @param proposalID
 */
export const downloadEqualizerProposal = (proposalID: string) => {
  return {
    type: ACTION_TYPES.DOWNLOAD_FILE,
    payload: axios.get(`proposals/submissions/${proposalID}`, {
      params: { type: "EqualizerProposal" },
      responseType: "blob",
    }),
  };
};
